import { motion } from "framer-motion";
import { Linkedin, ArrowUpRight, Lock } from "lucide-react";
import { scrollToSection } from "../lib/site";

const timeline = [
  {
    role: "Founder",
    company: "THEBOMBAYHOUSE",
    location: "Mumbai",
    period: "2024 — Present",
    highlight: "Marketplace launch & growth consultancy for Indian brands.",
  },
  {
    role: "Business Partner",
    company: "CraftHomeIndia",
    location: "Mumbai",
    period: "2024 — Present",
    highlight: "Scaling the brand on Meta and Amazon alongside the founders.",
  },
  {
    role: "Lead Business Analyst → Lead Incident Manager → Sr L2 Analyst",
    company: "EFI",
    location: "Bengaluru",
    period: "2015 — 2024 · 9 yrs",
    highlight:
      "IT security programmes, accountability for global outages, and executive Power BI reporting for CFO & CIO reviews.",
  },
  {
    role: "Server Technical Consultant",
    company: "Hewlett-Packard",
    location: "Bangalore",
    period: "2010 — 2015",
  },
  {
    role: "Apple Tech Support",
    company: "Hutchison 3G",
    location: "Mumbai",
    period: "2009 — 2010",
  },
  {
    role: "Advance Resolution Expert",
    company: "Dell",
    location: "DOC Process",
    period: "2007 — 2009",
  },
  {
    role: "Team Coach · Line Trainer · Windows Update Engineer",
    company: "Stream Global · eFunds · Tracmail",
    location: "Early career",
    period: "2003 — 2007",
  },
];

export const Founder = ({ unlocked }) => {
  return (
    <section
      id="founder"
      data-testid="founder-section"
      className="relative py-28 lg:py-40 bg-ink border-y border-coal/10 overflow-hidden"
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        {!unlocked ? (
          <motion.div
            data-testid="founder-locked"
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="border border-coal/15 px-8 py-20 lg:py-28 text-center max-w-3xl mx-auto"
          >
            <Lock size={34} strokeWidth={1.5} className="mx-auto text-gold" />
            <span className="block mt-6 text-xs font-bold uppercase tracking-[0.25em] text-coal/40">
              04 — The Founder
            </span>
            <h2 className="font-display font-900 tracking-tight leading-none text-3xl sm:text-4xl lg:text-5xl mt-4">
              23 years behind{" "}
              <span className="font-accent italic font-medium text-terracotta">
                the house.
              </span>
            </h2>
            <p className="mt-6 text-base sm:text-lg leading-relaxed text-coal/60 max-w-xl mx-auto">
              The founder's full story — photo, journey and two decades of
              operator experience — unlocks after a verified enquiry.
            </p>
            <button
              data-testid="founder-unlock-cta"
              onClick={() => scrollToSection("#contact")}
              className="mt-10 inline-flex items-center gap-2.5 bg-terracotta text-night font-bold text-sm uppercase tracking-[0.15em] px-8 py-5 hover:bg-gold transition-colors duration-300"
            >
              Send an Enquiry to Unlock
              <ArrowUpRight size={17} strokeWidth={2.5} />
            </button>
          </motion.div>
        ) : (
        <>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <div className="absolute -top-4 -left-4 w-full h-full border border-gold/50 pointer-events-none" />
            <div className="relative overflow-hidden">
              <motion.img
                src="/founder.jpg"
                alt="Amit Ahuuja — Founder, TheBombayHouse"
                data-testid="founder-photo"
                initial={{ scale: 1.15 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
                className="w-full aspect-[4/5] object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-night/40 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 flex items-end justify-between">
                <div>
                  <div className="font-display font-900 text-white text-2xl tracking-tight" data-testid="founder-photo-name">
                    Amit Ahuuja
                  </div>
                  <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-white/70 mt-1">
                    Founder — TheBombayHouse
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          <div>
            <motion.span
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-xs font-bold uppercase tracking-[0.25em] text-coal/40"
            >
              04 — The Founder
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="font-display font-900 tracking-tight leading-[0.95] text-4xl sm:text-5xl lg:text-6xl mt-4"
              data-testid="founder-headline"
            >
              The operator behind{" "}
              <span className="font-accent italic font-medium text-terracotta">
                the house.
              </span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="mt-8 font-accent italic text-xl sm:text-2xl leading-snug text-coal/75 border-l-2 border-terracotta pl-6"
            >
              "Most brands don't fail on marketplaces because their product is
              weak. They fail because nobody told them which door to knock —
              and which one to kick."
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
              className="mt-8 text-base sm:text-lg leading-relaxed text-coal/60 max-w-lg"
            >
              Amit Ahuuja founded TheBombayHouse after 23 years across IT
              operations, incident leadership and business analysis — watching
              great Indian brands struggle to crack Amazon, Shopify and quick
              commerce. His approach is simple: honest strategy first, joint
              execution second, full accountability when it matters. Every
              engagement — from a crash course to a yearly partnership — is
              led by him personally.
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
              data-testid="founder-crafthome-note"
              className="mt-6 text-sm leading-relaxed text-coal/55 max-w-lg border border-coal/15 px-5 py-4"
            >
              <span className="font-bold text-gold uppercase tracking-[0.15em] text-xs block mb-1.5">
                Also building
              </span>
              He practices what he preaches — as Business Partner at{" "}
              <span className="font-bold text-coal/80">CraftHomeIndia</span>,
              scaling the brand on Meta and Amazon to new heights alongside its
              founders.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="mt-12 flex flex-wrap items-center gap-4"
            >
              <a
                href="https://www.linkedin.com/in/amitahuuja/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="founder-linkedin-link"
                className="inline-flex items-center gap-2.5 border border-coal/25 text-coal font-bold text-sm uppercase tracking-[0.15em] px-7 py-4 hover:border-terracotta hover:text-terracotta transition-colors duration-300"
              >
                <Linkedin size={17} strokeWidth={2} />
                Connect on LinkedIn
              </a>
              <button
                data-testid="founder-cta-btn"
                onClick={() => scrollToSection("#contact")}
                className="inline-flex items-center gap-2.5 bg-terracotta text-night font-bold text-sm uppercase tracking-[0.15em] px-7 py-4 hover:bg-gold transition-colors duration-300"
              >
                Work With Amit
                <ArrowUpRight size={17} strokeWidth={2.5} />
              </button>
            </motion.div>
          </div>
        </div>

        <div className="mt-24 lg:mt-32 border-t border-coal/10 pt-16 lg:pt-20">
          <motion.span
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-xs font-bold uppercase tracking-[0.25em] text-coal/40"
          >
            Career — 23 Years
          </motion.span>
          <motion.h3
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="font-display font-900 tracking-tight leading-none text-3xl sm:text-4xl lg:text-5xl mt-4 max-w-2xl"
            data-testid="timeline-heading"
          >
            23 years to{" "}
            <span className="font-accent italic font-medium text-terracotta">
              TheBombayHouse.
            </span>
          </motion.h3>

          <div className="mt-14 border-l border-coal/15" data-testid="founder-timeline">
            {timeline.map((t, i) => (
              <motion.div
                key={`${t.role}-${t.period}`}
                data-testid={`timeline-item-${i}`}
                initial={{ opacity: 0, x: -24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] }}
                className="group relative pl-8 lg:pl-12 pb-10 last:pb-0"
              >
                <span className="absolute -left-[5px] top-2 w-2.5 h-2.5 rotate-45 bg-gold group-hover:bg-terracotta transition-colors duration-300" />
                <div className="flex flex-col lg:flex-row lg:items-baseline lg:justify-between gap-1 lg:gap-8">
                  <div>
                    <h4 className="font-display font-800 text-xl sm:text-2xl tracking-tight">
                      {t.role}
                    </h4>
                    <p className="text-sm text-coal/55 mt-1">
                      <span className="font-bold text-coal/75">{t.company}</span>
                      {" · "}
                      {t.location}
                    </p>
                    {t.highlight && (
                      <p className="text-sm leading-relaxed text-coal/50 mt-2 max-w-xl">
                        {t.highlight}
                      </p>
                    )}
                  </div>
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-gold whitespace-nowrap shrink-0">
                    {t.period}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
        </>
        )}
      </div>
    </section>
  );
};
